import { env } from "@src/env.mjs";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Template } from "types";
import { Layout } from "./imports";
import generateAssetUrl from "@utils/generateAssetUrl";
import CustomImage from "@components/shared/CustomImage";
import { getGrapesEditorInstance, loadGrapesJsScripts } from "lib/grapes";
import { useApps } from "@hooks/useApps";
import { serverEndpoints } from "@utils/serverEndpoints";
import { ProjectData } from "grapesjs";

interface LayoutPlayerProps {
  layoutInfo?: Layout | Template;
  muted?: boolean;
  renderContent?: boolean;
}

interface Font {
  family: string;
  url: string;
}

const LayoutPlayer = ({ layoutInfo, muted, renderContent = false }: LayoutPlayerProps) => {
  if (renderContent) return <LayoutPlayerRenderer layoutInfo={layoutInfo} muted={muted} />
  return <CustomImage
    src={generateAssetUrl(`thumbnail/${layoutInfo?.id}`) + "?q=" + Date.now()}
    alt={layoutInfo?.name ?? ""}
    className="max-h-32 w-full object-cover"
    fallback={
      <LayoutPlayerRenderer layoutInfo={layoutInfo} muted={muted} />
    }
  />
}

const LayoutPlayerRenderer = ({ layoutInfo, muted = true }: LayoutPlayerProps) => {
  const [html, setHtml] = useState("")
  const [css, setCss] = useState("")
  const { apps } = useApps()
  const [zoom, setZoom] = useState(1);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const layoutRef = useRef<HTMLDivElement | null>(null);

  const _apps = apps.map(app => ({ id: app.id, url: `${env.NEXT_PUBLIC_SCRIPTS_URL}${app.id}/latest.min.js?v=${Date.now()}` }))

  const [loaded, setLoaded] = useState(false)

  useLayoutEffect(() => {
    if (loaded) return
    const serverUrl = serverEndpoints.serverUrl;
    loadGrapesJsScripts(_apps, {
      onLoad: () => {
        setLoaded(true)
        initializeEditor({
          pluginsOpts: { serverUrl },
        });
      },
      onError: () => { },
    })
  }, [apps, loaded])

  function initializeEditor({
    pluginsOpts,
  }: {
    pluginsOpts?: Record<string, any>;
  } = {}) {
    const newEditor = getGrapesEditorInstance({
      readOnly: true,
      apps: _apps,
      fromElement: false,
      headless: true,
      pluginsOpts,
      jsInHtml: true,
      canvas: {
        // TODO : import font dynamically when selected
        // styles: fonts.map((font) => font.url),
        scripts: ["https://cdn.tailwindcss.com"],
      },
    })
    newEditor.DomComponents.addType("svg", {});

    function getBuilderData() {
      const builderData = layoutInfo?.builder_data
      if (!builderData) return undefined
      if (typeof builderData === "string") return JSON.parse(builderData) as ProjectData
      return JSON.parse(JSON.stringify(builderData)) as ProjectData
    }

    const projectData = getBuilderData()

    if (projectData)
      newEditor.loadProjectData(projectData);

    // window.editor = newEditor;
    const html = newEditor.getHtml();
    const css = newEditor.getCss();
    if (html) setHtml(html);
    if (css) setCss(css);
  }

  useEffect(() => {
    if (!muted) return;
    const mediaElements = containerRef.current?.querySelectorAll('[data-js="videoPlayer"]');

    mediaElements?.forEach(media => {
      if ("muted" in media) {
        media.muted = true;
      }
    });
  }, [muted])

  useEffect(() => {
    if (layoutInfo && containerRef.current) {
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;

      if (layoutInfo.height && layoutInfo.width) {
        const vertScale = containerHeight / layoutInfo.height;
        const horzScale = containerWidth / layoutInfo.width;
        setZoom(Math.min(vertScale, horzScale, 1)); // Prevent upscaling beyond 1
      }
    }
  }, [layoutInfo, containerRef]);

  const { data: fontData } = useQuery<Font[]>({
    queryKey: ["fonts-list"],
    refetchInterval: Infinity,
    gcTime: Infinity,
    networkMode: "offlineFirst",
    refetchOnMount: false,
    async queryFn() {
      return fetch((env.NEXT_PUBLIC_SERVER_URL ?? "") + "/getFonts").then(res => res.json())
    }
  })

  useEffect(() => {
    if (!fontData || !html.length || !css.length) return;

    const prevFragment = document.getElementById("style_div");
    if (prevFragment) {
      document.head.removeChild(prevFragment);
    }

    const fragment = document.createElement("div");
    fragment.id = "style_div";

    for (const font of fontData) {
      if (html.includes(font.family) || css.includes(font.family)) {
        const link = document.createElement("link");
        link.href = font.url;
        link.rel = "stylesheet";
        fragment.appendChild(link);
      }
    }

    document.head.appendChild(fragment);

    return () => {
      const cleanupFragment = document.getElementById("style_div");
      if (cleanupFragment) {
        document.head.removeChild(cleanupFragment);
      }
    };
  }, [fontData, html, css])

  useEffect(() => {
    if (layoutInfo) {
      const _prevScript = document.getElementById("tailwind_script");
      if (!_prevScript) {
        const tailwind = document.createElement("script");
        tailwind.id = "tailwind_script";
        tailwind.src = "https://cdn.tailwindcss.com";
        document.head.appendChild(tailwind);

        tailwind.onload = () => {
          onTailwindLoaded();
        };
      } else {
        onTailwindLoaded();
      }

    }
  }, [layoutInfo]);

  function onTailwindLoaded() {
    if (!layoutInfo) return;
    function ensureMutedYouTubeEmbed(htmlString: string) {
      const regex = /(src=")(youtube\.com\/[^"]*)(?=")/gi;
      const newHtmlString = htmlString.replace(regex, (match, prefix, videoPath) => {
        const currentSrc = `${prefix}${videoPath}"`;
        if (currentSrc.includes('?muted=1"') || currentSrc.includes('&muted=1"')) {
          return match; // muted=1 is already present
        } else if (videoPath.includes('?')) {
          const mutedHtml = `${prefix}${videoPath}&muted=1"`; // Add &muted=1 if other parameters exist
          console.log(mutedHtml)
          return mutedHtml
        } else {
          const mutedHtml = `${prefix}${videoPath}?muted=1"`; // Add ?muted=1 if no parameters exist
          console.log(mutedHtml)
          return mutedHtml
        }
      });
      return newHtmlString;
    }

    function addMutedToVideoElement(htmlString: string) {
      const regex = /(<video[^>]*)(>)/gi;
      const newHtmlString = htmlString.replace(regex, (match, tagStart, tagEnd) => {
        if (!tagStart.includes('muted')) {
          const mutedHtml = `${tagStart} muted${tagEnd}`;
          console.log(mutedHtml)
          return mutedHtml
        }
        return match; // muted attribute is already present
      });
      return newHtmlString;
    }

    function maybeAddAutoPlay(htmlString: string) {
      if (!muted) return htmlString;
      return addMutedToVideoElement(ensureMutedYouTubeEmbed(htmlString));
    }

    const style = document.createElement("style");
    style.id = `layout_style`;
    style.innerHTML = layoutInfo.css ?? "";

    document.head.appendChild(style);

    const layoutDiv = layoutRef.current
    if (layoutDiv) {
      let htmlToRender = layoutInfo.html ?? "";
      let scripts: any = [];
      try {
        const pattern = /<script>([\s\S]*?)<\/script>/gi;
        scripts = htmlToRender.match(pattern);
        htmlToRender = htmlToRender.replace(pattern, "");

        htmlToRender = htmlToRender.replace("<body", "<div");
        htmlToRender = htmlToRender.replace("</body>", "</div>");
      } catch (e) {
      }
      layoutDiv.innerHTML = maybeAddAutoPlay(htmlToRender);
      const child = layoutDiv.children[0];
      if (child) {
        child.setAttribute(
          "style",
          `height: ${layoutInfo.height}px;width: ${layoutInfo.width}px`,
        );
      }

      const _prevFragment = document.getElementById("script_fragment")
      if (_prevFragment) document.parentElement?.removeChild(_prevFragment)

      const fragment = document.createElement("div");
      fragment.id = "script_fragment"

      if (scripts) {
        for (let i = 0; i < scripts.length; i++) {
          const script = scripts[i];
          const scriptContent = script
            .replace(`<script>`, ``)
            .replace(`</script>`, ``);
          const scriptEl = document.createElement("script");
          scriptEl.textContent =
            `(function script${i}() {` + scriptContent + " }())";
          fragment.appendChild(scriptEl);
        }
      }

      document.head.appendChild(fragment);
    }
  }

  return (
    <div
      ref={containerRef}
      id="backgroundElem"
      className="flex h-full w-full items-center justify-center bg-white"
    >
      <div
        className="bg-white"
        style={{
          transform: `scale(${zoom})`,
          transformOrigin: "center",
          width: layoutInfo?.width + "px",
          height: layoutInfo?.height + "px",
          position: "relative",
        }}
      >
        <div ref={layoutRef} className="text-3xl">Loading...</div>
      </div>
    </div>
  );
};
export default LayoutPlayer;
